<!-- src/lib/components/CommandPalette.svelte -->
<!-- ⌘K command palette — Linear-quality quick navigation + actions -->
<script lang="ts">
  import { pmStore, hydrateBoardDetail, hydrateBoards } from '../stores/pm.svelte.ts';
  import { api } from '../api.ts';
  import { loadRecentCards, onRecentCardsUpdated, pushRecentCard } from '../card-recents.ts';
  import { fly, fade } from 'svelte/transition';
  import { tick } from 'svelte';
  import type { Card, Project } from '../types.ts';

  interface Props {
    onClose: () => void;
    onOpenCard: (id: string) => void;
    onNavigate: (tab: 'overview' | 'board' | 'table' | 'gallery' | 'canvas' | 'agents' | 'activity' | 'tasks' | 'pulse' | 'chat' | 'brand' | 'drive' | 'studio' | 'dev') => void;
    navSection?: string;
    onFileOpen?: (path: string) => void;
  }
  let { onClose, onOpenCard, onNavigate, navSection, onFileOpen }: Props = $props();

  type ActionItem = {
    id: string;
    label: string;
    description?: string;
    icon: string;
    category: string;
    shortcut?: string;
    action: () => void;
  };

  let query  = $state('');
  let selected = $state(0);
  let inputEl: HTMLInputElement | null = $state(null);

  let recentItems = $state(loadRecentCards());
  let fileResults = $state<{path: string; label: string}[]>([]);

  async function searchFiles(q: string) {
    if (!q.startsWith('/') && !q.startsWith('.') && !q.includes('.')) {
      fileResults = [];
      return;
    }
    try {
      const results = await api<{path: string}[]>(
        `/api/v1/files?q=${encodeURIComponent(q)}&limit=10`
      );
      fileResults = results.map(r => ({
        path: r.path,
        label: r.path.split('/').pop() ?? r.path,
      }));
    } catch {
      fileResults = [];
    }
  }

  async function createCard() {
    const title = window.prompt('New task title:');
    if (!title?.trim()) return;
    if (!pmStore.activeBoardId || !pmStore.activeProjectId) {
      window.alert('Open a project first.');
      return;
    }

    try {
      if (!pmStore.boardDetails[pmStore.activeBoardId]) {
        await hydrateBoardDetail(pmStore.activeBoardId);
      }
      const detail = pmStore.boardDetails[pmStore.activeBoardId];
      const firstCol = detail?.columns[0];
      if (!firstCol) throw new Error('No columns on board');

      const card = await api<Pick<Card, 'id' | 'title' | 'human_id'>>('/api/v1/cards', {
        method: 'POST',
        body: JSON.stringify({
          project_id: pmStore.activeProjectId,
          column_id: firstCol.id,
          title: title.trim(),
          priority: 'none',
        }),
      });
      pushRecentCard(card);
      recentItems = loadRecentCards();
      onClose();
    } catch (error) {
      console.warn('Failed to create card:', error);
    }
  }

  async function createProject() {
    const name = window.prompt('New project name:');
    if (!name?.trim()) return;
    if (!pmStore.activeWorkspaceId) {
      window.alert('Open a workspace first.');
      return;
    }

    const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || `project-${Date.now()}`;
    try {
      const project = await api<Project>('/api/v1/projects', {
        method: 'POST',
        body: JSON.stringify({
          workspace_id: pmStore.activeWorkspaceId,
          name: name.trim(),
          slug,
        }),
      });
      pmStore.projects = [...pmStore.projects, project];
      await hydrateBoards(project.id);
      onNavigate('board');
      onClose();
    } catch (error) {
      console.warn('Failed to create project:', error);
    }
  }

  function dispatchAgent() {
    onNavigate('agents');
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent('agentboard:focus-task-form'));
    });
    onClose();
  }

  // Build flat action list from current state
  const baseActions: ActionItem[] = $derived((() => {
    const createActions: ActionItem[] = [
      {
        id: 'create-card',
        label: 'New task',
        description: 'Create card in active project',
        icon: '+',
        category: 'Create',
        shortcut: 'N',
        action: () => { void createCard(); },
      },
      {
        id: 'dispatch-agent',
        label: 'Dispatch agent',
        description: 'Go to Agents and focus the dispatch form',
        icon: '◎',
        category: 'Create',
        action: dispatchAgent,
      },
      {
        id: 'create-project',
        label: 'New project',
        description: 'Create project in active workspace',
        icon: '◉',
        category: 'Create',
        action: () => { void createProject(); },
      },
    ];

    const recentActions: ActionItem[] = recentItems.map((recent) => ({
      id: `recent-${recent.id}`,
      label: recent.title,
      description: recent.human_id ? `Recent · ${recent.human_id}` : 'Recent',
      icon: '◷',
      category: 'Recent',
      action: () => {
        onOpenCard(recent.id);
        pushRecentCard(recent);
        recentItems = loadRecentCards();
        onClose();
      },
    }));

    const actions: ActionItem[] = [
      ...createActions,
      ...recentActions,
      // Navigation
      {
        id: 'nav-board',
        label: 'Go to Board',
        icon: '⊞',
        category: 'Navigate',
        shortcut: '⌘1',
        action: () => { onNavigate('board'); onClose(); },
      },
      {
        id: 'nav-table',
        label: 'Go to Table',
        icon: '≡',
        category: 'Navigate',
        shortcut: '⌘2',
        action: () => { onNavigate('table'); onClose(); },
      },
      {
        id: 'nav-studio',
        label: 'Go to Studio',
        icon: '◈',
        category: 'Navigate',
        shortcut: '⌘3',
        action: () => { onNavigate('studio'); onClose(); },
      },
      {
        id: 'nav-dev',
        label: 'Go to Dev',
        icon: '⌥',
        category: 'Navigate',
        shortcut: '⌘4',
        action: () => { onNavigate('dev'); onClose(); },
      },
      {
        id: 'nav-canvas',
        label: 'Go to Canvas',
        icon: '◈',
        category: 'Navigate',
        action: () => { onNavigate('canvas'); onClose(); },
      },
      {
        id: 'nav-agents',
        label: 'Go to Agents',
        icon: '◎',
        category: 'Navigate',
        action: () => { onNavigate('agents'); onClose(); },
      },
      {
        id: 'action-toggle-sidebar',
        label: 'Toggle sidebar',
        icon: '▤',
        category: 'Actions',
        shortcut: '⌘\\',
        action: () => { window.dispatchEvent(new CustomEvent('agentboard:toggle-sidebar')); onClose(); },
      },
      {
        id: 'action-toggle-terminal',
        label: 'Toggle terminal',
        icon: '>_',
        category: 'Actions',
        shortcut: 'Ctrl `',
        action: () => { window.dispatchEvent(new CustomEvent('agentboard:toggle-terminal')); onClose(); },
      },
      {
        id: 'action-focus-mode',
        label: 'Focus mode',
        icon: '⊡',
        category: 'Actions',
        shortcut: 'F',
        action: () => { window.dispatchEvent(new CustomEvent('agentboard:focus-mode')); onClose(); },
      },
      {
        id: 'action-shortcuts',
        label: 'Show shortcuts',
        icon: '?',
        category: 'Actions',
        shortcut: '?',
        action: () => { window.dispatchEvent(new CustomEvent('agentboard:show-shortcuts')); onClose(); },
      },
    ];

    // Project actions
    for (const project of pmStore.projects) {
      actions.push({
        id: `project-${project.id}`,
        label: project.name,
        description: 'Open project',
        icon: '◉',
        category: 'Projects',
        action: () => { hydrateBoards(project.id); onNavigate('board'); onClose(); },
      });
    }

    // Card search (from all loaded board details)
    const allCards = Object.values(pmStore.boardDetails)
      .flatMap(bd => bd.columns.flatMap(c => c.cards));
    for (const card of allCards) {
      actions.push({
        id: `card-${card.id}`,
        label: card.title,
        description: `#${card.id.slice(-4)} · ${card.priority !== 'none' ? card.priority : ''}`,
        icon: '○',
        category: 'Cards',
        action: () => {
          onOpenCard(card.id);
          pushRecentCard({ id: card.id, title: card.title, human_id: card.human_id ?? null });
          recentItems = loadRecentCards();
          onClose();
        },
      });
    }

    return actions;
  })());

  const filtered: ActionItem[] = $derived((() => {
    if (!query.trim()) return baseActions.slice(0, 12);
    const q = query.toLowerCase();
    return baseActions
      .filter(a => a.label.toLowerCase().includes(q) || a.category.toLowerCase().includes(q))
      .slice(0, 12);
  })());

  // Group by category
  const grouped: Map<string, ActionItem[]> = $derived((() => {
    const m = new Map<string, ActionItem[]>();
    for (const item of filtered) {
      const arr = m.get(item.category) ?? [];
      arr.push(item);
      m.set(item.category, arr);
    }
    return m;
  })());

  // Flat list for keyboard navigation
  const flatItems: ActionItem[] = $derived(filtered);

  $effect(() => {
    // Reset selection when filter changes; trigger file search in dev section
    const q = query;
    selected = 0;
    if (navSection === 'dev') {
      void searchFiles(q);
    } else {
      fileResults = [];
    }
  });

  $effect(() => {
    // Focus input on mount
    tick().then(() => inputEl?.focus());
  });

  $effect(() => {
    recentItems = loadRecentCards();
    return onRecentCardsUpdated((items) => {
      recentItems = items;
    });
  });

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      selected = Math.min(selected + 1, flatItems.length - 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      selected = Math.max(selected - 1, 0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (fileResults.length > 0) {
        const file = fileResults[selected];
        if (file) { onFileOpen?.(file.path); onClose(); }
      } else {
        flatItems[selected]?.action();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  }

  function handleOverlayClick(e: MouseEvent) {
    if (e.target === e.currentTarget) onClose();
  }
</script>

<!-- svelte-ignore a11y_click_events_have_key_events -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div class="palette-overlay" transition:fade={{ duration: 120 }} onclick={handleOverlayClick}>
  <div class="palette" transition:fly={{ y: -20, duration: 180, opacity: 1 }} role="dialog" aria-label="Command palette">
    <div class="search-row">
      <span class="search-icon">⌕</span>
      <input
        bind:this={inputEl}
        bind:value={query}
        onkeydown={handleKeydown}
        placeholder="Search cards, projects, actions…"
        class="search-input"
        autocomplete="off"
        spellcheck="false"
      />
      <kbd class="esc-hint">esc</kbd>
    </div>

    <div class="results">
      {#if fileResults.length > 0}
        <div class="group-label">Files</div>
        {#each fileResults as file, i}
          <!-- svelte-ignore a11y_click_events_have_key_events -->
          <!-- svelte-ignore a11y_no_static_element_interactions -->
          <div
            class="result-item"
            class:selected={i === selected}
            onclick={() => { onFileOpen?.(file.path); onClose(); }}
            onmouseenter={() => { selected = i; }}
          >
            <span class="item-icon">⎇</span>
            <span class="item-label">{file.label}</span>
            <span class="item-desc">{file.path}</span>
            {#if i === selected}
              <kbd class="enter-hint">↵</kbd>
            {/if}
          </div>
        {/each}
      {:else if flatItems.length === 0}
        <div class="no-results">No results for "{query}"</div>
      {:else}
        {#each [...grouped.entries()] as [category, items]}
          <div class="group-label">{category}</div>
          {#each items as item}
            {@const idx = flatItems.indexOf(item)}
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div
              class="result-item"
              class:selected={idx === selected}
              onclick={item.action}
              onmouseenter={() => { selected = idx; }}
            >
              <span class="item-icon">{item.icon}</span>
              <span class="item-label">{item.label}</span>
              {#if item.description}
                <span class="item-desc">{item.description}</span>
              {/if}
              {#if item.shortcut}
                <kbd class="palette-kbd">{item.shortcut}</kbd>
              {/if}
              {#if idx === selected}
                <kbd class="enter-hint">↵</kbd>
              {/if}
            </div>
          {/each}
        {/each}
      {/if}
    </div>

    <div class="palette-footer">
      <span><kbd>↑↓</kbd> navigate</span>
      <span><kbd>↵</kbd> select</span>
      <span><kbd>esc</kbd> close</span>
    </div>
  </div>
</div>

<style>
  .palette-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.6);
    z-index: 100;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 15vh;
    backdrop-filter: blur(4px);
  }

  .palette {
    width: 560px;
    max-width: calc(100vw - 2rem);
    background: #13141a;
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 12px;
    box-shadow:
      0 0 0 1px rgba(0,0,0,0.5),
      0 24px 60px rgba(0,0,0,0.6),
      0 0 40px oklch(0.72 0.28 310 / 0.05);
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .search-row {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.875rem 1rem;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }

  .search-icon {
    font-size: 1.1rem;
    color: rgba(255,255,255,0.25);
    flex-shrink: 0;
    line-height: 1;
  }

  .search-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: #e8eaf0;
    font-size: 0.925rem;
    font-family: system-ui, sans-serif;
    caret-color: oklch(0.80 0.26 310);
  }

  .search-input::placeholder { color: rgba(255,255,255,0.2); }

  .esc-hint {
    font-size: 0.65rem;
    color: rgba(255,255,255,0.2);
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 3px;
    padding: 0.1rem 0.35rem;
    font-family: system-ui, sans-serif;
  }

  .results {
    overflow-y: auto;
    max-height: 380px;
    padding: 0.375rem 0;
  }

  .group-label {
    font-size: 0.62rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(255,255,255,0.2);
    padding: 0.5rem 1rem 0.25rem;
    font-weight: 600;
  }

  .result-item {
    display: flex;
    align-items: center;
    gap: 0.625rem;
    padding: 0.5rem 1rem;
    cursor: pointer;
    transition: background 80ms ease;
  }

  .result-item.selected,
  .result-item:hover {
    background: rgba(255,255,255,0.05);
  }

  .result-item.selected {
    background: oklch(0.72 0.28 310 / 0.07);
  }

  .item-icon {
    font-size: 0.875rem;
    color: rgba(255,255,255,0.3);
    flex-shrink: 0;
    width: 1.25rem;
    text-align: center;
  }

  .item-label {
    font-size: 0.85rem;
    color: #d0d4e0;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .item-desc {
    font-size: 0.7rem;
    color: rgba(255,255,255,0.25);
    font-family: 'JetBrains Mono', monospace;
    flex-shrink: 0;
  }

  .enter-hint {
    font-size: 0.62rem;
    color: oklch(0.78 0.26 310 / 0.58);
    background: oklch(0.72 0.28 310 / 0.07);
    border: 1px solid oklch(0.72 0.28 310 / 0.18);
    border-radius: 3px;
    padding: 0.1rem 0.35rem;
    font-family: system-ui, sans-serif;
    flex-shrink: 0;
  }

  .palette-kbd {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.60rem;
    background: oklch(0.17 0.025 275);
    border: 1px solid oklch(0.30 0.05 275 / 0.40);
    border-radius: 3px;
    padding: 0.08rem 0.35rem;
    color: oklch(0.72 0.05 310 / 0.75);
    white-space: nowrap;
    margin-left: auto;
    flex-shrink: 0;
  }

  .no-results {
    padding: 2rem;
    text-align: center;
    color: rgba(255,255,255,0.2);
    font-size: 0.85rem;
  }

  .palette-footer {
    display: flex;
    gap: 1.25rem;
    padding: 0.625rem 1rem;
    border-top: 1px solid rgba(255,255,255,0.04);
    background: rgba(255,255,255,0.01);
  }

  .palette-footer span {
    font-size: 0.65rem;
    color: rgba(255,255,255,0.18);
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }

  .palette-footer kbd {
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 3px;
    padding: 0.05rem 0.3rem;
    font-family: system-ui, sans-serif;
    font-size: 0.65rem;
    color: rgba(255,255,255,0.35);
  }
</style>
